import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
    Body,
    Container,
    Content as DefaultContent,
    ContentContainer as DefaultContentContainer
} from '../../containers/DefaultStyles/Details';
import NextAction from '../../containers/Widget/NextAction';
import Overlay from '../../containers/Widget/Overlay';
import OverlayPartner from '../../containers/Widget/OverlayPartner';
import OverlayPublisher from '../../containers/Widget/OverlayPublisher';
import WidgetHeader from '../../containers/Widget/WidgetHeader';
import { Footer } from '../../containers/DefaultStyles/Shared';

import ShareDrawer from '../../components/ShareDrawer';
import { Title } from '../../components/Shared/TitleSuccess';

import { widthEm } from '../../utils/size';
import { getImageUrl } from '../../utils/imageLoading';
import { copyFor } from '../../copy/copy';

const Content = styled(DefaultContent)`
    ${() => widthEm() <= 30 && `display: inline;`};
    ${props => props.hideBorders && `margin-top: 0;`};
`;

const ContentContainer = styled(DefaultContentContainer)`
    margin-top: 0;
    height: 100%;
`;

const Description = styled.div`
    object-fit: contain;
    font-family: Lato;
    line-height: 1.45;
    color: #000;
    ${props => (props.hasDirectionsLink ? 'font-size: 14px;' : 'font-size: 16px;')};

    @media (max-width: 36.5em) {
        ${props => (props.hasDirectionsLink ? 'font-size: 13px;' : 'font-size: 14px;')};
    }

    @media (max-width: 30em) {
        ${props => (props.hasDirectionsLink ? 'font-size: 14px;' : 'font-size: 16px;')};
        line-height: 1.35;
    }
`;

const ThankYouText = styled.span`
    color: #333;

    a {
        color: #ff245e;
        text-decoration: none;
    }
`;
const IframeComponent = styled.div``;

const ImageContainer = styled.div`
    text-align: center;
    margin: 0 auto;
    width: auto;
    max-height: ${props =>
        !props.hasNextAction ? `calc(100% - 100px);` : `calc(100% - 50px);`};
`;

const Image = styled.img`
    max-height: 100%;
    max-width: 100%;
    margin: 0 auto;
`;

const ThankYouImage = props => {
    if (props.endCardPictureLink) {
        return (
            <ImageContainer hasNextAction={props.hasNextAction}>
                <a
                    style={{ margin: '0 auto' }}
                    href={props.endCardPictureLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Image src={props.endCardPictureUrl} />
                </a>
            </ImageContainer>
        );
    } else {
        return (
            <ImageContainer>
                <Image src={props.endCardPictureUrl} />
            </ImageContainer>
        );
    }
};
ThankYouImage.propTypes = {
    endCardPictureLink: PropTypes.string,
    endCardPictureUrl: PropTypes.string,
    hasNextAction: PropTypes.bool
};

const ConfirmationCard = props => (
    <Container>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <Body>
            {!props.hideBorders && <WidgetHeader />}
            <Content hideBorders={props.hideBorders}>
                {props.fullAddress && (
                    <IframeComponent>{props.iframeComponent}</IframeComponent>
                )}
                <ContentContainer>
                    {props.thankYouTitle ? (
                        <Title>{props.thankYouTitle}</Title>
                    ) : (
                        props.titleComponent
                    )}
                    {props.hasImage && props.endCardPictureUrl ? (
                        <ThankYouImage
                            endCardPictureLink={props.endCardPictureLink}
                            endCardPictureUrl={props.endCardPictureUrl}
                        />
                    ) : (
                        <div>
                            <Description hasDirectionsLink={props.getDirectionsComponent}>
                                {!props.hideSubtitle &&
                                    !props.thankYouText &&
                                    (props.useLocalRepresentative ? (
                                        <span>
                                            {copyFor(
                                                'endCardFeedback',
                                                'contactRep',
                                                undefined,
                                                undefined,
                                                { peopleCount: props.peopleCount }
                                            )}
                                        </span>
                                    ) : (
                                        <span>
                                            {copyFor(
                                                'endCardFeedback',
                                                'changeHeadlines',
                                                undefined,
                                                undefined,
                                                { peopleCount: props.peopleCount }
                                            )}
                                        </span>
                                    ))}
                                {props.bodyComponent ? (
                                    props.bodyComponent
                                ) : (
                                    <ThankYouText
                                        dangerouslySetInnerHTML={{
                                            __html: props.thankYouText
                                        }}
                                    />
                                )}
                            </Description>
                            {props.fullAddress && props.getDirectionsComponent}
                        </div>
                    )}
                </ContentContainer>
            </Content>
            {!props.hideBorders && (
                <Footer>
                    <ShareDrawer
                        fullwidth={props.fullwidth}
                        rightAlign={props.rightAlign}
                        newsletterSignUpVisible={true}
                    />
                </Footer>
            )}
        </Body>
        <NextAction hideFooter={props.hideBorders} />
    </Container>
);

ConfirmationCard.propTypes = {
    bodyComponent: PropTypes.element,
    endCardPictureUrl: PropTypes.string,
    endCardPictureLink: PropTypes.string,
    fullAddress: PropTypes.string,
    fullwidth: PropTypes.bool,
    getDirectionsComponent: PropTypes.element,
    hasImage: PropTypes.bool,
    hideBorders: PropTypes.bool,
    hideSubtitle: PropTypes.bool,
    iframeComponent: PropTypes.element,
    peopleCount: PropTypes.string,
    rightAlign: PropTypes.bool,
    thankYouText: PropTypes.string,
    thankYouTitle: PropTypes.string,
    titleComponent: PropTypes.element.isRequired,
    useLocalRepresentative: PropTypes.bool
};

export default connect(
    (state, props) => {
        return {
            hasNextAction: state.widget.confirmationInfo.hasNextAction,
            endCardPictureLink: state.widget.confirmationInfo.pictureLink,
            endCardPictureUrl: getImageUrl(state.widget.confirmationInfo.pictureId),
            hasImage: !!state.widget.confirmationInfo.pictureId,
            hideBorders: state.widget.confirmationInfo.hideBorders,
            thankYouText: props.thankYouText || state.widget.confirmationInfo.text,
            thankYouTitle: props.thankYouTitle || state.widget.confirmationInfo.title,
            useLocalRepresentative:
                props.useLocalRepresentative ||
                state.widget.confirmationInfo.useLocalRepresentative
        };
    },
    {}
)(ConfirmationCard);
